import httpClient from '~/lib/http-client';

// ─── Claimable generations (DEV-166): admin handoff of a chat to a recipient ───

/** Claim state the admin share surface renders. */
export interface ClaimStatus {
  claimable: boolean;
  claimedBy: { email: string; displayName: string | null } | null;
  claimedAt: string | null;
  expiresAt: string | null;
  expired: boolean;
  revoked: boolean;
}

/** What the public claim landing page knows before the visitor signs in. */
export interface ClaimPreview {
  state: 'valid' | 'expired' | 'revoked' | 'claimed' | 'invalid';
  chatId: string | null;
  description: string | null;
  hasPreview: boolean;
}

/** ADMIN: mark a chat claimable; returns the claim URL (rotates on re-enable). */
export async function apiEnableClaim(chatId: string): Promise<{ url: string; expiresAt: string }> {
  const { data } = await httpClient.post<{ url: string; expiresAt: string }>(`/api/chats/${chatId}/claim/enable`);
  return data;
}

/** ADMIN: toggle claimable off — immediately revokes the shared link. */
export async function apiDisableClaim(chatId: string): Promise<{ ok: true }> {
  const { data } = await httpClient.post<{ ok: true }>(`/api/chats/${chatId}/claim/disable`);
  return data;
}

/** ADMIN: current claim state for the share surface. */
export async function apiGetClaimStatus(chatId: string): Promise<ClaimStatus> {
  const { data } = await httpClient.get<ClaimStatus>(`/api/chats/${chatId}/claim/status`);
  return data;
}

/** PUBLIC: resolve a claim token for the landing page (no auth needed). */
export async function apiGetClaimPreview(token: string): Promise<ClaimPreview> {
  const { data } = await httpClient.get<ClaimPreview>(`/api/claims/${token}`);
  return data;
}

/** AUTHED: consume the token — transfers the generation to the current user. */
export async function apiClaimGeneration(token: string): Promise<{ chatId: string }> {
  const { data } = await httpClient.post<{ chatId: string }>(`/api/claims/${token}/claim`);
  return data;
}

/** A generation this admin handed off that was claimed. */
export interface HandedOffItem {
  chatId: string;
  description: string | null;
  claimedBy: string | null;
  claimedAt: string;
}

/** ADMIN: generations handed off + claimed — the sidebar trace after transfer. */
export async function apiGetHandedOff(): Promise<HandedOffItem[]> {
  const { data } = await httpClient.get<{ handedOff: HandedOffItem[] }>('/api/claims/handed-off');
  return data.handedOff;
}

/** Read-only archive of one handed-off generation (transcript + claim info). */
export interface HandedOffChat {
  chatId: string;
  description: string | null;
  claimedBy: { email: string; displayName: string | null } | null;
  claimedAt: string | null;
  hasPreview: boolean;
  messages: unknown[];
}

/** ADMIN: full archive view of a generation this admin handed off. */
export async function apiGetHandedOffChat(chatId: string): Promise<HandedOffChat> {
  const { data } = await httpClient.get<HandedOffChat>(`/api/claims/handed-off/${chatId}`);
  return data;
}
